import { OUT_MS, type Season } from './seasons';

/**
 * How a seasonal splash takes over from the native one (seasons in
 * `seasons.ts`).
 *
 * The native splash is a still image and stays one; animating it would mean a
 * store release for both apps. Instead the web page paints an exact copy of the
 * splash the moment it loads and animates that, so the hand-over from the native
 * image is invisible. "Exact" is load-bearing: both platforms show the 1366px
 * square artwork aspect-fit to the screen (iOS `scaleAspectFit`, Android
 * `CENTER_INSIDE`), so the copy is drawn in that same 1366 square, sized
 * `min(100vw, 100vh)` and centred, with the M traced from the artwork itself.
 *
 * Who sees it: the native app, once per launch per season (sessionStorage
 * outlives a reload but not the app being killed), never with reduced motion.
 * A tap skips it.
 *
 * 🚨 The title is held back on a launch that may play it (`splashTitleHeld` in
 * `server.ts` says why: the iOS cover comes down on the title, before the page
 * has painted, and that was a white flash). This script puts it back.
 */

export const SPLASH_ATTR = 'data-splash';
/** Which season's overlay shows, so a preview page and the layout's own never both appear. */
export const SEASON_ATTR = 'data-splash-season';
export const SPLASH_PREVIEW_COOKIE = 'mucka_splash_preview';

/**
 * The earliest the animation starts, as ms since navigation start. The native
 * splash auto-hides at 1200ms after launch and the WebView starts navigating a
 * moment after launch, so this is roughly when the splash has gone.
 */
const START_AT_MS = 1250;
/**
 * And never before the page has finished loading plus this, because that is
 * when the iOS cover comes down when there is no title, and the cover takes
 * 200ms to fade.
 */
const AFTER_LOAD_MS = 250;
/** If `load` never comes, start anyway. */
const START_BACKSTOP_MS = 5000;

/**
 * Runs during HTML parse, before the overlay markup, so a launch that should not
 * play it never paints it. Only touches `<html>` (an attribute) and the title:
 * the overlay itself is React's, and removing it here would be a hydration
 * mismatch. `hold` shows the still M; `play` runs the animation.
 *
 * `title` is the one the server held back, or null. `force` plays it in any
 * browser, every time, straight away (the preview page).
 */
export function splashBootScript(opts: {
  season: Season;
  endsAt: number;
  title: string | null;
  force?: boolean;
}): string {
  const { season, endsAt, title, force = false } = opts;
  const seenKey = `mucka.splash.${season.id}`;
  const safetyMs = season.outAtMs + OUT_MS + 1500;
  return `(function(){
var T=${JSON.stringify(title)},named=function(){if(T&&!document.title)document.title=T;};
try{
var h=document.documentElement,f=${force};
if(!f){
if(navigator.userAgent.indexOf('MuckaApp')<0||Date.now()>=${endsAt})return named();
if(window.matchMedia&&matchMedia('(prefers-reduced-motion: reduce)').matches)return named();
if(sessionStorage.getItem('${seenKey}'))return named();
sessionStorage.setItem('${seenKey}','1');
}
h.setAttribute('${SEASON_ATTR}','${season.id}');h.setAttribute('${SPLASH_ATTR}','hold');
var done=function(){h.removeAttribute('${SPLASH_ATTR}');h.removeAttribute('${SEASON_ATTR}');named();};
var go=function(){if(h.getAttribute('${SPLASH_ATTR}')!=='hold')return;h.setAttribute('${SPLASH_ATTR}','play');named();setTimeout(done,${safetyMs});};
var later=function(){setTimeout(go,f?${AFTER_LOAD_MS}:Math.max(${AFTER_LOAD_MS},${START_AT_MS}-performance.now()));};
if(document.readyState==='complete')later();else window.addEventListener('load',later);
setTimeout(go,${START_BACKSTOP_MS});
document.addEventListener('animationend',function(e){if(e.animationName==='ms-out')done();});
document.addEventListener('pointerdown',done,true);
}catch(e){named();}})();`;
}
